import React, { Component } from "react";
import { Alert, Modal, Spin, Tag, message } from "antd";
import { ArrowRightOutlined, MergeCellsOutlined } from "@ant-design/icons";
import axios from "axios";
import { connect } from "react-redux";
import { ButtonItem } from "../../../../Components/Items";
import ModalUnificarEmpleado, { COLOR_COINCIDENCIA, textoBiometria } from "./ModalUnificarEmpleado";

// Mismo molde que los historiales del empleado (EditarEmpleado/ModalHistorialRelacionLaboral.js):
// Modal sin footer y tabla HTML cruda. NO se usa TableItem porque hardcodea `pagination` y `scroll`.
const TH = {
  padding: "6px 10px", fontSize: 11, fontWeight: 600, textAlign: "center",
  border: "1px solid #4a6a7a", whiteSpace: "nowrap", color: "#fff", background: "#5a7a8a",
};
const TD = {
  border: "1px solid #e0e0e0", padding: "6px 10px", textAlign: "left",
  fontSize: 12, verticalAlign: "middle",
};

const codigoTexto = (codigo) => (codigo !== null && codigo !== undefined ? String(codigo).padStart(4, "0") : "—");

const Lado = ({ emp }) => {
  if (!emp) return null;
  return (
    <>
      <div style={{ fontWeight: 600 }}>
        {emp.nombre}
        {emp.estado !== 1 && <Tag style={{ marginLeft: 6 }}>Deshabilitado</Tag>}
      </div>
      <div style={{ color: "#666" }}>
        Código {codigoTexto(emp.codigo)}{emp.rfc ? ` · RFC ${emp.rfc}` : ""}
        {emp.acumulados > 0 && <Tag color="blue" style={{ marginLeft: 6 }}>Con historial de nómina</Tag>}
      </div>
      {emp.relojes?.length > 0 && (
        <div>
          {emp.relojes.map((r, i) => <Tag key={i} color="cyan" style={{ marginBottom: 2 }}>{r.reloj} · PIN {r.pin}</Tag>)}
          <span style={{ color: "#888" }}>{textoBiometria(emp.biometria)}</span>
        </div>
      )}
      {/* Con reloj en los dos, se propone conservar al de más checadas: el número tiene que verse. */}
      {(emp.relojes?.length > 0 || emp.checadas > 0) && (
        <div style={{ color: "#666" }}>{(emp.checadas || 0).toLocaleString("es-MX")} checada(s)</div>
      )}
    </>
  );
};

/**
 * Parejas de empleados que parecen el mismo trabajador dado de alta dos veces: uno con asignación
 * de reloj checador y otro con nombre igual o parecido (erp_backend/Utils/unificarEmpleados.utils.js
 * `detectarParejas`). El backend propone cuál se queda —el de nómina— y cada pareja se revisa y
 * se unifica por separado en ModalUnificarEmpleado, que es donde están los candados.
 */
class ModalUnificarDuplicados extends Component {
  state = {
    loading: false,
    parejas: [],
    seleccion: null,
    huboCambios: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ parejas: [], seleccion: null, huboCambios: false });
      this.consultar();
    }
  }

  async consultar() {
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "empleados/datosParaUnificar",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (res.data.success !== 2) {
        message.error(res.data.mensaje || "No se pudieron buscar los duplicados");
        this.setState({ loading: false });
        return;
      }
      this.setState({ parejas: res.data.parejas || [], loading: false });
    } catch (e) {
      this.setState({ loading: false });
      message.error("Error de conexión al buscar duplicados");
    }
  }

  render() {
    const { loading, parejas, seleccion, huboCambios } = this.state;
    const hayAmbiguas = parejas.some((p) => p.ambigua);

    return (
      <>
        <Modal
          title="Unificar empleados duplicados"
          visible={this.props.visible}
          onCancel={() => this.props.onCerrar(huboCambios)}
          footer={null}
          width={window.innerWidth > 1000 ? 980 : "95%"}
        >
          <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
            Empleados con reloj checador que tienen otro registro con el mismo nombre (o parecido).
            Al unificar, lo del reloj —asignación con el mismo PIN, checadas, turnos, huellas y
            rostros— pasa al que se queda y el duplicado se deshabilita. La nómina no se mueve.
            Si los dos tienen reloj, se propone conservar al de más checadas.
          </div>
          {hayAmbiguas && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 10 }}
              message="Hay empleados que coinciden con más de un registro (marcados como Ambiguo). Revisa con cuidado cuál es la persona correcta."
            />
          )}
          <Spin spinning={loading}>
            <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={TH}>Duplicado (se deshabilita)</th>
                    <th style={TH}></th>
                    <th style={TH}>Se queda</th>
                    <th style={TH}>Nombre</th>
                    <th style={TH}></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && parejas.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ ...TD, textAlign: "center", color: "#aaa", padding: 24 }}>
                        No se encontraron empleados duplicados. Si sabes de alguno, usa el botón
                        "Unificar con…" en su renglón.
                      </td>
                    </tr>
                  )}
                  {parejas.map((p, i) => (
                    <tr key={`${p.duplicadoId}-${p.conservarId}`} style={{ background: i % 2 === 0 ? undefined : "#fafafa" }}>
                      <td style={TD}><Lado emp={p.duplicado} /></td>
                      <td style={{ ...TD, textAlign: "center", color: "#999" }}><ArrowRightOutlined /></td>
                      <td style={TD}><Lado emp={p.conservar} /></td>
                      <td style={{ ...TD, textAlign: "center" }}>
                        <Tag color={COLOR_COINCIDENCIA[p.coincidencia?.puntaje]}>{p.coincidencia?.texto}</Tag>
                        {p.ambigua && <Tag color="red">Ambiguo</Tag>}
                      </td>
                      <td style={{ ...TD, textAlign: "center" }}>
                        <ButtonItem
                          htmlType="button"
                          size="small"
                          azul
                          icon={<MergeCellsOutlined />}
                          onClick={() => this.setState({ seleccion: { duplicadoId: p.duplicadoId, conservarId: p.conservarId } })}
                        >
                          Revisar y unificar
                        </ButtonItem>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Spin>
        </Modal>
        <ModalUnificarEmpleado
          visible={!!seleccion}
          duplicadoId={seleccion?.duplicadoId}
          conservarId={seleccion?.conservarId}
          onCerrar={(unificado) => {
            this.setState({ seleccion: null, huboCambios: huboCambios || unificado });
            if (unificado) this.consultar();
          }}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalUnificarDuplicados);
